import { useCallback, useEffect, useRef, useState } from 'react'
import { Paperclip, ShieldAlert } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { listItems, type Item } from '@/features/dossier/api'
import {
  GradeChip, LabeledBar, Pill, SeverityBadge, SubHeader, fmtDate, label,
} from '@/features/dossier/components/primitives'
import { api } from '@/shared/api/client'
import {
  attachSurfaceFiles, linkEvidence, listSurfaceFiles, markSurfaceFile, recordSurface,
  type Surface, type SurfaceFile,
} from './api'

const CATEGORIES = ['LEGAL', 'INTEGRITY', 'FINANCIAL', 'PERFORMANCE', 'POLITICAL',
  'PERSONAL_PUBLIC', 'OPERATIONAL', 'COALITION']
const ROLES = ['PRIMARY', 'CORROBORATING', 'CONTEXT', 'REBUTTAL']

/** The rationale minimum, mirrored from the server so the form can say so before it refuses. */
const MIN_RATIONALE = 40

/**
 * Recording an exposure, and linking what stands behind it.
 *
 * ## Why an item in the dossier is not itself a scandal
 *
 * A `COURT_RECORD` on a subject's file is a FACT the platform holds. A surface is a CLAIM
 * somebody is making about a target — "undeclared shareholding in a firm that won a county
 * tender" — with a severity, a defensibility and a name attached to the judgement. The two
 * are different objects on purpose: one court record can support three different claims, and
 * three court records can support one. Scandals & Legal lists SURFACES, which is why filing
 * an item never made a row appear there.
 *
 * ## Evidence strength has no input
 *
 * It is computed from the linked items — distinct sources first, then the best Admiralty
 * grade, then volume — and there is no field for it here because there is none on the
 * server. An analyst who could type it would type 90.
 *
 * ## Two different things can be attached, and the screen keeps them apart
 *
 * **Evidence** is a graded `intel_item` from the subject's dossier file: it has a source, an
 * Admiralty pair and a custody date, and linking it MOVES the strength.
 *
 * **Working files** are the analyst's own material on the write-up — the annotated chart, the
 * photograph of the noticeboard, the draft one-pager. They can be attached while the exposure
 * is being recorded and at any time after, several at once, any type, and they do NOT move
 * the strength. A dragged-in file has no source and no grade; if it counted, a screenshot and
 * a certified judgment would sit on the same footing. The control says so where it is.
 */
export function SurfaceEditor({ targetId, subjectId, surfaces, onChanged }: {
  targetId: string
  /** The target's dossier subject. Evidence can only be an item on their own file. */
  subjectId: string
  surfaces: Surface[]
  onChanged: () => void
}) {
  const [recording, setRecording] = useState(false)
  /** The surface being read. Master–detail: one list, one pane, nothing rendered twice. */
  const [active, setActive] = useState<string | null>(null)
  const [linking, setLinking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [categoryCode, setCategoryCode] = useState('LEGAL')
  const [surface, setSurface] = useState('')
  const [detail, setDetail] = useState('')
  const [severity, setSeverity] = useState(50)
  const [defensibility, setDefensibility] = useState(50)
  const [publicExposure, setPublicExposure] = useState(20)
  const [recommendation, setRecommendation] = useState('')
  const [rationale, setRationale] = useState('')

  const [items, setItems] = useState<Item[]>([])
  const [roleCode, setRoleCode] = useState('PRIMARY')
  const [note, setNote] = useState('')

  // Files chosen BEFORE the surface exists. They upload immediately after it is created,
  // because "attach while recording" is one act to the person doing it even though it is two
  // calls underneath.
  const [pending, setPending] = useState<File[]>([])
  const [fileCaption, setFileCaption] = useState('')
  const newFiles = useRef<HTMLInputElement>(null)

  const [files, setFiles] = useState<Record<string, SurfaceFile[]>>({})
  const [markingFile, setMarkingFile] = useState<string | null>(null)
  const [markReason, setMarkReason] = useState('')

  const loadFiles = useCallback(async (surfaceId: string) => {
    try {
      const rows = await listSurfaceFiles(surfaceId)
      setFiles((current) => ({ ...current, [surfaceId]: rows }))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => {
    surfaces.forEach((s) => { void loadFiles(s.id) })
    // Keyed by id, so a re-render after a link does not refetch every surface's files.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surfaces.map((s) => s.id).join(','), loadFiles])

  /**
   * Open a stored file. NOT a plain `<a href>`: the download route answers with a 302 to the
   * object store and an anchor cannot carry the bearer token to get that far.
   */
  async function openFile(storageFileId: string) {
    try {
      const presigned = await api.get<{ url: string }>(`/files/${storageFileId}/url`)
      window.open(presigned.url, '_blank', 'noopener')
    } catch (err) {
      setError(explain(err))
    }
  }

  async function uploadTo(surfaceId: string, chosen: File[], caption: string) {
    if (chosen.length === 0) return
    await attachSurfaceFiles(surfaceId, chosen, caption)
    await loadFiles(surfaceId)
  }

  async function markFile(attachmentId: string, surfaceId: string, dependable: boolean) {
    setBusy(true)
    setError(null)
    try {
      await markSurfaceFile(attachmentId, dependable, markReason)
      setMarkingFile(null)
      setMarkReason('')
      await loadFiles(surfaceId)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const loadItems = useCallback(async () => {
    try {
      setItems((await listItems({ subjectId, size: '100' })).content)
    } catch (err) {
      setError(explain(err))
    }
  }, [subjectId])

  useEffect(() => { if (linking) void loadItems() }, [linking, loadItems])

  // Selecting the newest surface after one is recorded means the operator lands on what they
  // just wrote rather than on whatever happened to sort first.
  useEffect(() => {
    if (active && surfaces.some((s) => s.id === active)) return
    setActive(surfaces[0]?.id ?? null)
  }, [surfaces, active])

  const rationaleShort = rationale.trim().length < MIN_RATIONALE

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (rationaleShort || !surface.trim()) return
    setBusy(true)
    setError(null)
    try {
      const saved = await recordSurface(targetId, {
        categoryCode,
        surface: surface.trim(),
        detail: detail.trim() || null,
        severity,
        defensibility,
        publicExposure,
        recommendation: recommendation.trim() || null,
        rationale: rationale.trim(),
      })
      // The exposure has to exist before a file can hang off it, so this is a second call —
      // but it is one act to the operator and a failure here must not read as "nothing
      // happened", which is why the exposure is already saved when it runs.
      await uploadTo(saved.id, pending, fileCaption)
      setRecording(false)
      setSurface(''); setDetail(''); setRecommendation(''); setRationale('')
      setPending([]); setFileCaption('')
      if (newFiles.current) newFiles.current.value = ''
      onChanged()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function link(surfaceId: string, itemId: string) {
    setBusy(true)
    setError(null)
    try {
      await linkEvidence(surfaceId, { itemId, roleCode, note: note.trim() || null })
      setNote('')
      onChanged()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const selected = surfaces.find((s) => s.id === active) ?? surfaces[0] ?? null
  const selectedFiles = selected ? files[selected.id] ?? [] : []
  const linkedIds = new Set(selected?.evidence.map((e) => e.itemId) ?? [])

  return (
    <div className="space-y-2">
      {error && <ErrorStrip message={error} />}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <SubHeader>Attack surfaces</SubHeader>
        <button type="button" onClick={() => setRecording((v) => !v)}
                className="rounded border border-border px-2 py-1 text-[11px]">
          {recording ? 'Close' : 'Record an exposure'}
        </button>
      </div>

      {recording && (
        <form onSubmit={submit} className="space-y-2 rounded border border-border p-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Category
              </span>
              <select value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)}
                      className="h-7 rounded border border-input bg-background px-2
                                 text-[12px]">
                {CATEGORIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
              </select>
              <span className="text-[10px] text-muted-foreground">
                Only Legal, Integrity and Financial reach Scandals &amp; Legal.
              </span>
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                The exposure, in one line
              </span>
              <input value={surface} onChange={(e) => setSurface(e.target.value)} required
                     placeholder="Undeclared shareholding in a firm that won a county tender"
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>
          </div>

          <div className="grid gap-2 sm:grid-cols-3">
            {([
              ['Severity', severity, setSeverity, 'How damaging it would be'],
              ['Defensibility', defensibility, setDefensibility, 'How easily THEY can answer it'],
              ['Public exposure', publicExposure, setPublicExposure, 'How widely it is known already'],
            ] as const).map(([name, value, set, hint]) => (
              <label key={name} className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {name} — {value}
                </span>
                <input type="range" min={0} max={100} value={value}
                       onChange={(e) => set(Number(e.target.value))} />
                <span className="text-[10px] text-muted-foreground">{hint}</span>
              </label>
            ))}
          </div>

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Rationale
            </span>
            <textarea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={2}
                      placeholder="Why this is an exposure and why the severity is what it is."
                      className="rounded border border-input bg-background px-2 py-1
                                 text-[12px]" />
            <span className={`text-[10px] ${rationaleShort ? 'text-amber-600'
              : 'text-muted-foreground'}`}>
              {/* The server refuses under 40. An unexplained severity ends up in a briefing. */}
              {rationale.trim().length}/{MIN_RATIONALE} characters minimum
            </span>
          </label>

          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Detail (optional)
              </span>
              <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={2}
                        className="rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Recommendation (optional)
              </span>
              <textarea value={recommendation} rows={2}
                        onChange={(e) => setRecommendation(e.target.value)}
                        className="rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
            </label>
          </div>

          <div className="space-y-1 rounded border border-dashed border-border p-2">
            <div className="flex items-center gap-1.5">
              <Paperclip className="size-3.5 text-muted-foreground" />
              <span className="text-[11px] font-medium">Supporting files (optional)</span>
            </div>
            <input value={fileCaption} onChange={(e) => setFileCaption(e.target.value)}
                   placeholder="Caption for the files (optional)"
                   className="h-7 w-full rounded border border-input bg-background px-2
                              text-[11px]" />
            <input ref={newFiles} type="file" multiple
                   onChange={(e) => setPending(Array.from(e.target.files ?? []))}
                   className="text-[11px]" />
            <p className="text-[10px] text-muted-foreground">
              {/* Said at the control, because this is the thing that would otherwise be
                  assumed. */}
              Any type, several at once. These support the WRITE-UP and do <b>not</b> count
              towards evidence strength — a file has no source and no grade. Anything that
              should count goes on the subject's dossier file first and is linked as evidence
              below. Once attached a file cannot be deleted; mark it not dependable instead.
            </p>
            {pending.length > 0 && (
              <p className="text-[10px] text-muted-foreground">
                {pending.length} file{pending.length === 1 ? '' : 's'} will be attached once
                the exposure is recorded.
              </p>
            )}
          </div>

          <button type="submit" disabled={busy || rationaleShort || !surface.trim()}
                  className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                             text-primary-foreground disabled:opacity-50">
            {busy ? 'Recording…' : 'Record the exposure'}
          </button>
        </form>
      )}


      {surfaces.length === 0 ? (
        <p className="py-2 text-[12px] text-muted-foreground">
          No exposures recorded. An exposure is a CLAIM about this target — the items on their
          dossier file are the evidence you attach to it, so filing an item does not create
          one. A surface needs a rationale of at least {MIN_RATIONALE} characters: an
          unexplained severity ends up in a press briefing.
        </p>
      ) : (
        <div className="grid gap-2 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          {/* THE LIST. Scannable and ranked, one line per exposure — the question it answers
              is "which of these should I be looking at", and a wall of expanded cards
              answers it worst. */}
          <ul className="axis-table-scroll max-h-[26rem] space-y-1 overflow-y-auto">
            {surfaces.map((s) => {
              const on = selected?.id === s.id
              return (
                <li key={s.id}>
                  <button type="button" onClick={() => setActive(s.id)}
                          aria-current={on}
                          className={`w-full rounded border p-2 text-left ${on
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-muted/40'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[12px] font-medium leading-snug">{s.surface}</span>
                      <Pill className="shrink-0 border-border">{label(s.categoryCode)}</Pill>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5
                                    text-[10px] text-muted-foreground">
                      <span>Severity <b className="text-foreground">{s.severity}</b></span>
                      {/* Derived, and the number the whole register is ordered by. */}
                      <span>Evidence <b className={s.evidenceStrength === 0
                        ? 'text-amber-600' : 'text-emerald-600'}>{s.evidenceStrength}</b></span>
                      <span>{(files[s.id] ?? []).length} file
                        {(files[s.id] ?? []).length === 1 ? '' : 's'}</span>
                      {s.statusCode === 'STALE'
                        ? <SeverityBadge severity="HIGH" />
                        : <Pill className="border-border">{label(s.statusCode)}</Pill>}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>

          {/* THE DETAIL. Everything about ONE exposure, in one place — which is what the
              previous layout could not do, because it rendered every surface twice: once
              read-only and once again with the controls. */}
          {selected && (
            <div className="space-y-2 rounded border border-border p-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-[13px] font-medium">{selected.surface}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {label(selected.categoryCode)} · recorded {fmtDate(selected.firstSeenAt)}
                    {selected.lastReviewedAt
                      && ` · last reviewed ${fmtDate(selected.lastReviewedAt)}`}
                  </p>
                </div>
                {selected.statusCode === 'STALE'
                  ? <SeverityBadge severity="HIGH" />
                  : <Pill className="border-border">{label(selected.statusCode)}</Pill>}
              </div>

              <div className="grid gap-2 sm:grid-cols-4">
                <LabeledBar label="Severity" value={selected.severity} />
                <LabeledBar label="Public exposure" value={selected.publicExposure} />
                <LabeledBar label="Defensibility" value={selected.defensibility} />
                <LabeledBar label="Evidence (derived)" value={selected.evidenceStrength}
                            tone="bg-emerald-500" />
              </div>

              {selected.statusCode === 'STALE' && (
                <p className="text-[11px] text-rose-600">
                  Evidence behind this was disputed or debunked. Review before using it.
                </p>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Rationale
                </p>
                <p className="text-[11px]">{selected.rationale}</p>
              </div>
              {selected.detail && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Detail
                  </p>
                  <p className="whitespace-pre-wrap text-[11px]">{selected.detail}</p>
                </div>
              )}
              {selected.recommendation && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Recommendation
                  </p>
                  <p className="whitespace-pre-wrap text-[11px]">{selected.recommendation}</p>
                </div>
              )}

              {/* The two attachable things, in two labelled sections, because only one of
                  them moves the number above. */}
              <div className="border-t border-border pt-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[11px] font-medium">
                    Evidence — graded items, moves the strength
                  </p>
                  <button type="button"
                          onClick={() => setLinking(linking === selected.id
                            ? null : selected.id)}
                          className="rounded border border-border px-2 py-0.5 text-[11px]">
                    {linking === selected.id ? 'Close' : 'Link an item'}
                  </button>
                </div>
                {selected.evidence.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">
                    Nothing linked, which is why the strength is 0. A surface with no evidence
                    is a suspicion, and the register says so on its face.
                  </p>
                ) : (
                  <ul className="mt-1 space-y-0.5">
                    {selected.evidence.map((e) => (
                      <li key={e.id} className="text-[11px] text-muted-foreground">
                        <Pill className="border-border">{label(e.roleCode)}</Pill>{' '}
                        <span className="font-mono text-[10px]">{e.itemId}</span>
                        {e.note && ` — ${e.note}`}
                      </li>
                    ))}
                  </ul>
                )}

                {linking === selected.id && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="flex flex-col gap-0.5">
                        <span className="text-[10px] uppercase tracking-wide
                                         text-muted-foreground">Role</span>
                        <select value={roleCode} onChange={(e) => setRoleCode(e.target.value)}
                                className="h-7 rounded border border-input bg-background px-2
                                           text-[12px]">
                          {ROLES.map((r) => <option key={r} value={r}>{label(r)}</option>)}
                        </select>
                      </label>
                      <label className="flex flex-1 flex-col gap-0.5">
                        <span className="text-[10px] uppercase tracking-wide
                                         text-muted-foreground">Note (optional)</span>
                        <input value={note} onChange={(e) => setNote(e.target.value)}
                               className="h-7 rounded border border-input bg-background px-2
                                          text-[12px]" />
                      </label>
                    </div>

                    {items.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground">
                        Nothing on this subject's file yet. Record it in Dossier → Manual
                        entry, or upload the document, and it becomes linkable here.
                      </p>
                    ) : (
                      <ul className="axis-table-scroll max-h-48 space-y-0.5 overflow-y-auto">
                        {items.map((item) => {
                          const already = linkedIds.has(item.id)
                          return (
                            <li key={item.id}>
                              <button type="button" disabled={busy || already}
                                      onClick={() => void link(selected.id, item.id)}
                                      className="flex w-full items-center gap-2 rounded px-1.5
                                                 py-1 text-left text-[11px] hover:bg-muted/60
                                                 disabled:opacity-50">
                                <GradeChip grade={item.grading} />
                                <span className="flex-1 truncate">
                                  {item.title ?? '(untitled)'}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {label(item.itemTypeCode)}
                                </span>
                                {already && (
                                  <span className="text-[10px] text-muted-foreground">
                                    linked
                                  </span>
                                )}
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      Two items from the same source count as one. Independence is the first
                      question a hostile reader asks.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-2">
                <p className="text-[11px] font-medium">
                  Working files — supporting the write-up, never the strength
                </p>
                <SurfaceFiles
                  surfaceId={selected.id}
                  files={selectedFiles}
                  busy={busy}
                  marking={markingFile}
                  reason={markReason}
                  onOpen={openFile}
                  onStartMark={setMarkingFile}
                  onReason={setMarkReason}
                  onMark={markFile}
                  onUpload={async (chosen, caption) => {
                    setBusy(true)
                    setError(null)
                    try {
                      await uploadTo(selected.id, chosen, caption)
                    } catch (err) {
                      setError(explain(err))
                    } finally {
                      setBusy(false)
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The files on one recorded exposure — and the control that adds more, at any time.
 *
 * Separate from the evidence list directly above it in the DOM on purpose: two lists that
 * looked alike would be read as one, and only one of them moves the strength.
 */
function SurfaceFiles({
  surfaceId, files, busy, marking, reason, onOpen, onStartMark, onReason, onMark, onUpload,
}: {
  surfaceId: string
  files: SurfaceFile[]
  busy: boolean
  marking: string | null
  reason: string
  onOpen: (storageFileId: string) => void
  onStartMark: (id: string | null) => void
  onReason: (value: string) => void
  onMark: (attachmentId: string, surfaceId: string, dependable: boolean) => void
  onUpload: (files: File[], caption: string) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [caption, setCaption] = useState('')
  const input = useRef<HTMLInputElement>(null)

  return (
    <div className="mt-1">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground
                         underline">
        <Paperclip className="size-3" />
        {files.length === 0 ? 'Attach files' : `Files (${files.length})`}
      </button>

      {(open || files.length > 0) && (
        <ul className="mt-1 space-y-1">
          {files.map((f) => (
            <li key={f.id} className={`rounded border p-1.5 ${f.dependable
              ? 'border-border' : 'border-rose-500/40 bg-rose-500/5'}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <button type="button" onClick={() => onOpen(f.storageFileId)}
                        className={`text-left text-[11px] ${f.dependable
                          ? 'text-primary underline'
                          : 'text-muted-foreground line-through'}`}>
                  {f.filename}
                </button>
                <span className="text-[10px] text-muted-foreground">
                  {fmtDate(f.attachedAt)}
                </span>
              </div>
              {f.caption && (
                <p className="text-[10px] text-muted-foreground">{f.caption}</p>
              )}
              {!f.dependable && (
                <p className="flex items-start gap-1 text-[10px] text-rose-700
                              dark:text-rose-400">
                  <ShieldAlert className="mt-0.5 size-3 shrink-0" />
                  Not dependable — {f.undependableReason}
                </p>
              )}
              {marking === f.id ? (
                <div className="mt-1 space-y-1">
                  {f.dependable && (
                    <input value={reason} onChange={(e) => onReason(e.target.value)}
                           placeholder="Why should nobody rely on this file?"
                           className="h-6 w-full rounded border border-input bg-background
                                      px-2 text-[10px]" />
                  )}
                  <div className="flex items-center gap-1.5">
                    <button type="button"
                            disabled={busy || (f.dependable && reason.trim().length < 20)}
                            onClick={() => onMark(f.id, surfaceId, !f.dependable)}
                            className="rounded bg-primary px-2 py-0.5 text-[10px]
                                       text-primary-foreground disabled:opacity-50">
                      {f.dependable ? 'Mark not dependable' : 'Restore'}
                    </button>
                    <button type="button" onClick={() => { onStartMark(null); onReason('') }}
                            className="rounded border border-border px-2 py-0.5 text-[10px]">
                      Cancel
                    </button>
                    {f.dependable && (
                      <span className="text-[10px] text-muted-foreground">
                        {reason.trim().length}/20 minimum
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => onStartMark(f.id)}
                        className="text-[10px] text-muted-foreground underline">
                  {f.dependable ? 'Mark not dependable' : 'Restore'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="mt-1 space-y-1 rounded border border-dashed border-border p-1.5">
          <input value={caption} onChange={(e) => setCaption(e.target.value)}
                 placeholder="Caption (optional)"
                 className="h-6 w-full rounded border border-input bg-background px-2
                            text-[10px]" />
          <input ref={input} type="file" multiple disabled={busy}
                 onChange={async (e) => {
                   const chosen = Array.from(e.target.files ?? [])
                   if (chosen.length === 0) return
                   await onUpload(chosen, caption)
                   setCaption('')
                   if (input.current) input.current.value = ''
                 }}
                 className="text-[10px]" />
          <p className="text-[10px] text-muted-foreground">
            Working files only — they do not change the evidence strength.
          </p>
        </div>
      )}
    </div>
  )
}
